// Wrapped so nothing here becomes a global lexical binding: these load as
// classic `defer` scripts, and a top-level `const`/`class` name colliding with
// another theme script is a parse-time SyntaxError that kills the whole file.
(() => {
  const MOBILE_QUERY = '(max-width: 767px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const ROOT_MARGIN = '200px';

  class HiraVideoSection extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
      this._ambientVideo = null;
      this._modalVideo = null;
      this._onIntersect = this._onIntersect.bind(this);
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
    }

    connectedCallback() {
      this._slot = this.querySelector('.hira-video__video-slot');
      this._trigger = this.querySelector('[data-video-open]');
      this._dialog = this.querySelector('[data-video-dialog]');
      this._closeButton = this.querySelector('[data-video-close]');
      this._modalSlot = this.querySelector('[data-video-modal-slot]');

      this._sources = this._pickSources(this._parseSources('videoDesktop'), this._parseSources('videoMobile'));

      this._trigger?.addEventListener('click', this._onTriggerClick);
      this._dialog?.addEventListener('click', this._onDialogClick);
      this._dialog?.addEventListener('close', this._onDialogClose);
      this._closeButton?.addEventListener('click', this._onCloseClick);

      if (this._shouldSkipAmbient()) return;

      this._observer = new IntersectionObserver(this._onIntersect, { rootMargin: ROOT_MARGIN });
      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;

      this._trigger?.removeEventListener('click', this._onTriggerClick);
      this._dialog?.removeEventListener('click', this._onDialogClick);
      this._dialog?.removeEventListener('close', this._onDialogClose);
      this._closeButton?.removeEventListener('click', this._onCloseClick);

      this._ambientVideo?.pause();
      this._modalVideo?.pause();
      this._ambientVideo = null;
      this._modalVideo = null;
    }

    _parseSources(key) {
      try {
        const parsed = JSON.parse(this.dataset[key] ?? '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    _pickSources(desktop, mobile) {
      if (window.matchMedia(MOBILE_QUERY).matches) {
        return mobile.length ? mobile : desktop;
      }
      return desktop.length ? desktop : mobile;
    }

    _shouldSkipAmbient() {
      if (!this._slot || !this._sources.length) return true;
      if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return true;
      return navigator.connection?.saveData === true;
    }

    _onIntersect(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this._observer?.disconnect();
      this._observer = null;
      this._buildAmbient();
    }

    _createVideo(className) {
      const video = document.createElement('video');
      video.className = className;
      video.playsInline = true;

      for (const { src, type } of this._sources) {
        if (!src) continue;
        const source = document.createElement('source');
        source.src = src;
        if (type) source.type = type;
        video.appendChild(source);
      }

      return video;
    }

    _buildAmbient() {
      const video = this._createVideo('hira-video__video');
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.preload = 'none';
      video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });

      this._ambientVideo = video;
      this._slot.appendChild(video);
      video.play().catch(() => {});
    }

    _onTriggerClick() {
      if (!this._dialog || !this._modalSlot || !this._sources.length) return;

      if (!this._modalVideo) {
        const video = this._createVideo('hira-video__modal-video');
        video.controls = true;
        video.preload = 'metadata';
        this._modalVideo = video;
        this._modalSlot.appendChild(video);
      }

      this._dialog.showModal();
      this._ambientVideo?.pause();
      this._modalVideo.play().catch(() => {});
    }

    _onCloseClick() {
      this._dialog?.close();
    }

    _onDialogClick(event) {
      if (event.target === this._dialog) this._dialog.close();
    }

    _onDialogClose() {
      if (this._modalVideo) {
        this._modalVideo.pause();
        this._modalVideo.currentTime = 0;
      }

      this._ambientVideo?.play().catch(() => {});
      this._trigger?.focus();
    }
  }

  if (!customElements.get('hira-video-section')) {
    customElements.define('hira-video-section', HiraVideoSection);
  }
})();
